// ---------------------------------------------------------------------------
// src/pages/RegressionDetail.tsx — Regression detection detail page (FR-B04)
//
// Shows all regression alerts for a PRD Execution with trend charts.
// Uses React.lazy for Recharts per NFR-003.
// ---------------------------------------------------------------------------

import { useMemo, lazy, Suspense } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";

// Lazy-load Recharts components (NFR-003: not in initial bundle)
const LazyLineChart = lazy(() =>
  import("recharts").then((m) => ({ default: m.LineChart }))
);
const LazyLine = lazy(() =>
  import("recharts").then((m) => ({ default: m.Line }))
);
const LazyXAxis = lazy(() =>
  import("recharts").then((m) => ({ default: m.XAxis }))
);
const LazyYAxis = lazy(() =>
  import("recharts").then((m) => ({ default: m.YAxis }))
);
const LazyTooltip = lazy(() =>
  import("recharts").then((m) => ({ default: m.Tooltip }))
);
const LazyResponsiveContainer = lazy(() =>
  import("recharts").then((m) => ({ default: m.ResponsiveContainer }))
);
const LazyCartesianGrid = lazy(() =>
  import("recharts").then((m) => ({ default: m.CartesianGrid }))
);
const LazyReferenceLine = lazy(() =>
  import("recharts").then((m) => ({ default: m.ReferenceLine }))
);

// --- Helpers ---

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMetric(metric: string): string {
  const labels: Record<string, string> = {
    completionRate: "Completion Rate",
    meanDuration: "Mean Duration",
    failureRate: "Failure Rate",
    needsHumanRate: "Needs Human Rate",
    meanCost: "Mean Cost",
    cacheEffectiveness: "Cache Effectiveness",
  };
  return labels[metric] ?? metric;
}

function formatValue(metric: string, value: number): string {
  if (
    metric === "completionRate" ||
    metric === "failureRate" ||
    metric === "needsHumanRate" ||
    metric === "cacheEffectiveness"
  ) {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (metric === "meanDuration") {
    const seconds = Math.round(value / 1000);
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }
  if (metric === "meanCost") {
    return `$${value.toFixed(4)}`;
  }
  return value.toFixed(4);
}

function severityColor(zScore: number): string {
  const absZ = Math.abs(zScore);
  if (absZ >= 5) return "text-red-400";
  if (absZ >= 4) return "text-orange-400";
  return "text-amber-400";
}

function severityBg(zScore: number): string {
  const absZ = Math.abs(zScore);
  if (absZ >= 5) return "bg-red-500/10 border-red-500/20";
  if (absZ >= 4) return "bg-orange-500/10 border-orange-500/20";
  return "bg-amber-500/10 border-amber-500/20";
}

// --- Trend chart for a single alert ---

function AlertTrendChart({
  alert,
}: {
  alert: {
    metric: string;
    baselineValue: number;
    currentValue: number;
    zScore: number;
    threshold: number;
  };
}) {
  // Simple 2-point chart showing baseline vs current
  const data = [
    { period: "Baseline (30d)", value: alert.baselineValue },
    { period: "Current (7d)", value: alert.currentValue },
  ];

  return (
    <Suspense fallback={<div className="h-32 bg-zinc-900/50 animate-pulse rounded" />}>
      <LazyResponsiveContainer width="100%" height={120}>
        <LazyLineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
          <LazyCartesianGrid strokeDasharray="3 3" stroke="#333" />
          <LazyXAxis dataKey="period" tick={{ fontSize: 10, fill: "#999" }} />
          <LazyYAxis tick={{ fontSize: 10, fill: "#999" }} />
          <LazyTooltip
            contentStyle={{ backgroundColor: "#1a1a2e", border: "1px solid #333" }}
            labelStyle={{ color: "#999" }}
          />
          <LazyReferenceLine
            y={alert.baselineValue}
            stroke="#666"
            strokeDasharray="3 3"
            label={{ value: "baseline", fill: "#666", fontSize: 10 }}
          />
          <LazyLine
            type="monotone"
            dataKey="value"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={{ fill: "#f59e0b", r: 4 }}
          />
        </LazyLineChart>
      </LazyResponsiveContainer>
    </Suspense>
  );
}

// --- Main component ---

export function RegressionDetail({ projectId }: { projectId: Id<"projects"> }) {
  const alerts = useQuery(api.regression.alertHistory, { projectId, limit: 50 });

  const activeAlerts = useMemo(
    () => (alerts ?? []).filter((a) => !a.dismissed),
    [alerts]
  );

  const dismissedAlerts = useMemo(
    () => (alerts ?? []).filter((a) => a.dismissed),
    [alerts]
  );

  // Group active alerts by model config
  const byModelConfig = useMemo(() => {
    const grouped = new Map<string, typeof activeAlerts>();
    for (const alert of activeAlerts) {
      const arr = grouped.get(alert.modelConfig) ?? [];
      arr.push(alert);
      grouped.set(alert.modelConfig, arr);
    }
    return grouped;
  }, [activeAlerts]);

  if (alerts === undefined) {
    return <div className="text-sm text-zinc-600">Loading regression data...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-zinc-100">Regression Detection</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Statistical regression analysis using z-scores. Alerts fire when 7-day metrics
          deviate significantly from the 30-day baseline.
        </p>
      </div>

      {activeAlerts.length === 0 ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-sm">No active regression alerts.</p>
          <p className="text-xs mt-2 text-zinc-700">
            The daily cron analyzes 6 metrics across all model configs.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(byModelConfig.entries()).map(([modelConfig, mcAlerts]) => (
            <div
              key={modelConfig}
              className="rounded-lg border border-zinc-800 overflow-hidden"
            >
              <div className="px-4 py-3 bg-zinc-900/60 border-b border-zinc-800 flex items-center gap-2">
                <span className="inline-flex items-center rounded-md bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 text-xs font-medium text-indigo-300">
                  {modelConfig}
                </span>
                <span className="text-xs text-zinc-500">
                  {mcAlerts.length} alert{mcAlerts.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="divide-y divide-zinc-800/50">
                {mcAlerts.map((alert) => (
                  <div
                    key={alert._id}
                    className={`px-4 py-4 ${severityBg(alert.zScore)} border-l-2`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${severityColor(alert.zScore)}`}>
                            {formatMetric(alert.metric)}
                          </span>
                          <span className="text-xs text-zinc-500">
                            z = {alert.zScore.toFixed(2)}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-400">
                          Baseline: {formatValue(alert.metric, alert.baselineValue)}
                          {" -> "}
                          Current: {formatValue(alert.metric, alert.currentValue)}
                        </div>
                        <div className="text-xs text-zinc-600">
                          {alert.baselineSessions} baseline sessions,{" "}
                          {alert.windowSessions} window sessions
                        </div>
                      </div>
                      <div className="text-xs text-zinc-600">
                        {formatDate(alert.detectedAt)}
                      </div>
                    </div>

                    <div className="mt-3">
                      <AlertTrendChart alert={alert} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {dismissedAlerts.length > 0 && (
        <details className="text-sm">
          <summary className="text-zinc-600 cursor-pointer hover:text-zinc-400">
            {dismissedAlerts.length} dismissed alert{dismissedAlerts.length !== 1 ? "s" : ""}
          </summary>
          <div className="mt-2 space-y-2">
            {dismissedAlerts.map((alert) => (
              <div
                key={alert._id}
                className="px-3 py-2 rounded bg-zinc-900/30 text-xs text-zinc-600"
              >
                {alert.modelConfig} / {formatMetric(alert.metric)} — z={alert.zScore.toFixed(2)}{" "}
                ({formatDate(alert.detectedAt)})
              </div>
            ))}
          </div>
        </details>
      )}

      <p className="text-xs text-zinc-600">
        Threshold: z-score &gt; 2.0 (configurable via meta key &quot;regressionThreshold&quot;).
        Metrics with &lt; 10 sessions are skipped.
      </p>
    </div>
  );
}
